/* ════ SEASONAL TOUCHES ════════════════════════════════════ */
// Small decorations on key dates: an emoji on the date in the planner header,
// a light tint down that day's column, and (around the day itself) one bit of
// ambient motion. Plus personal moments: birthday confetti and a palm on your
// last working day before approved leave. Everything can be switched off from
// the avatar menu (remembered per person, per browser) and motion is skipped
// for people who prefer reduced motion.
import { state } from './state.js';
import { $ } from './dom.js';
import { fdate, pdate, addD, isWE } from './utils/date.js';
import { isBankHoliday } from './bankholidays.js';
import { confetti } from './utils/confetti.js';
import { showToast } from './utils/toast.js';

const lc = s => String(s || '').toLowerCase();
const me = () => lc(state.currentUser?.email);
const mmdd = d => `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Easter Sunday for a year (anonymous Gregorian algorithm).
function easter(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  return new Date(y, Math.floor(n / 31) - 1, (n % 31) + 1);
}
// Autumn equinox (UK date); falls back to 23 Sep for years not listed.
const EQUINOX = { 2025: 22, 2026: 23, 2027: 23, 2028: 22, 2029: 22, 2030: 22 };

const FIXED = {
  '02-14': { e: '💘', k: 'val', name: "Valentine's Day" },
  '10-31': { e: '🎃', k: 'hal', name: 'Halloween' },
  '11-05': { e: '🎆', k: 'bon', name: 'Bonfire Night', fx: true },
  '12-24': { e: '✨', k: 'xmas', name: 'Christmas Eve' },
  '12-25': { e: '🎄', k: 'xmas', name: 'Christmas Day' },
  '12-26': { e: '🎁', k: 'xmas', name: 'Boxing Day' },
  '12-31': { e: '🎆', k: 'nye', name: "New Year's Eve", fx: true },
  '01-01': { e: '🥂', k: 'nye', name: "New Year's Day" },
};

const cache = new Map();
// The decoration for a date, or null.
export function specialDay(d) {
  const key = fdate(d);
  if (cache.has(key)) return cache.get(key);
  let s = FIXED[mmdd(d)] || null;
  if (!s) {
    const y = d.getFullYear(), diff = Math.round((new Date(y, d.getMonth(), d.getDate()) - easter(y)) / 864e5);
    if (diff === -47) s = { e: '🥞', k: 'pan', name: 'Pancake Day' };
    else if (diff === -2) s = { e: '🐣', k: 'easter', name: 'Good Friday' };
    else if (diff === 0) s = { e: '🥚', k: 'easter', name: 'Easter Sunday' };
    else if (diff === 1) s = { e: '🐣', k: 'easter', name: 'Easter Monday' };
    else if (d.getMonth() === 8 && d.getDate() === (EQUINOX[y] || 23)) s = { e: '🍂', k: 'aut', name: 'First day of autumn' };
  }
  cache.set(key, s);
  return s;
}

// "Today" for ambient effects. localStorage 'grantt_deco_today' = 'YYYY-MM-DD'
// previews another day (handy for checking Christmas in September).
function today() {
  try { const o = localStorage.getItem('grantt_deco_today'); if (o && /^\d{4}-\d{2}-\d{2}$/.test(o)) return pdate(o); } catch (e) { /* ignore */ }
  const t = new Date(); t.setHours(0, 0, 0, 0); return t;
}
const between = (d, from, to) => { const x = mmdd(d); return from <= to ? x >= from && x <= to : x >= from || x <= to; };

/* ── On / off ─────────────────────────────────────────── */
const prefKey = () => `grantt_deco_${me() || 'anon'}`;
export function decoOn() { try { return localStorage.getItem(prefKey()) !== 'off'; } catch (e) { return true; } }
function setDeco(on) {
  try { localStorage.setItem(prefKey(), on ? 'on' : 'off'); } catch (e) { /* private mode */ }
  applyDeco();
  showToast(on ? '✨ Seasonal decorations on' : 'Seasonal decorations off');
}
function applyDeco() {
  const on = decoOn();
  document.body.classList.toggle('deco-off', !on);
  const b = $('btn-deco'); if (b) b.textContent = `✨ Seasonal decorations: ${on ? 'On' : 'Off'}`;
  ambient();
}

/* ── Ambient effects, based on today's date ──────────── */
function ambient() {
  const t = today(), on = decoOn();
  const lights = on && between(t, '12-01', '01-06');     // fairy lights all December
  const snow = on && between(t, '12-20', '01-01');       // snow over the dates in Christmas week
  const bat = on && between(t, '10-24', '11-01');        // a bat in Halloween week
  const leaves = on && specialDay(t)?.k === 'aut';       // leaves drifting on the first day of autumn
  document.body.classList.toggle('season-snow', snow);
  document.body.classList.toggle('season-leaves', leaves);

  const tb = document.querySelector('.topbar');
  let fl = document.querySelector('.fairy-lights');
  if (lights && tb && !fl) {
    fl = document.createElement('div'); fl.className = 'fairy-lights'; fl.setAttribute('aria-hidden', 'true');
    const cols = ['#ED1F23', '#FFB400', '#0B8A3E', '#1A6FD4', '#FF7A45'];
    fl.innerHTML = Array.from({ length: 48 }, (_, i) => `<i style="color:${cols[i % 5]};animation-delay:${(i % 7) * 0.23}s"></i>`).join('');
    tb.appendChild(fl);
  } else if (!lights && fl) fl.remove();

  let bt = document.querySelector('.deco-bat');
  if (bat && !bt) { bt = document.createElement('div'); bt.className = 'deco-bat'; bt.textContent = '🦇'; bt.setAttribute('aria-hidden', 'true'); document.body.appendChild(bt); }
  else if (!bat && bt) bt.remove();
}

// Extra markup for the planner header (falling leaves on the first day of autumn).
export function headerExtras() {
  if (!document.body.classList.contains('season-leaves')) return '';
  return Array.from({ length: 7 }, (_, i) =>
    `<span class="deco-leaf" style="left:${8 + i * 13}%;animation-delay:${i * 0.9}s">${i % 2 ? '🍁' : '🍂'}</span>`).join('');
}

/* ── "Enjoy your break": last working day before your approved leave ── */
const isWorkday = d => !isWE(d) && !isBankHoliday(fdate(d));
export function breakEves() {
  const out = new Map();
  (state.leaveRequests || []).filter(r => lc(r.user_email) === me() && r.status === 'approved').forEach(r => {
    let d = addD(pdate(r.start), -1);
    for (let i = 0; i < 10 && !isWorkday(d); i++) d = addD(d, -1);
    out.set(fdate(d), r);
  });
  // A day that's itself inside approved leave isn't a "day before".
  (state.leaveRequests || []).filter(r => lc(r.user_email) === me() && r.status === 'approved')
    .forEach(r => { for (const k of [...out.keys()]) if (k >= r.start && k <= r.end) out.delete(k); });
  return out;
}

/* ── Once-a-day personal moments (after data has loaded) ── */
function onceToday(key) {
  const k = `grantt_${key}_${me()}`, v = fdate(today());
  try { if (localStorage.getItem(k) === v) return false; localStorage.setItem(k, v); } catch (e) { /* fine */ }
  return true;
}
export function seasonalAfterLoad() {
  applyDeco();
  if (!decoOn() || !me()) return;
  const t = today(), mine = state.resources.find(r => lc(r.user_email) === me());
  if (mine?.birthday === mmdd(t) && onceToday('bday')) {
    setTimeout(() => { confetti({ count: 200, emoji: ['🎂', '🎈', '🎉'] }); showToast(`🎂 Happy birthday, ${String(mine.name).split(' ')[0]}!`); }, 900);
  } else if (breakEves().has(fdate(t)) && onceToday('eyb')) {
    setTimeout(() => showToast('🌴 Last day before your break. Enjoy it!'), 900);
  }
}

export function initSeasonal() {
  const b = $('btn-deco');
  if (b) b.addEventListener('click', () => setDeco(!decoOn()));
  applyDeco();
}
